import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit, TemplateRef, ChangeDetectorRef } from '@angular/core';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CustomTableComponent } from '../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../shared/utils/table-types';
import { NotificationService } from '../../../shared/services/notification.service';
import { ConfirmService } from '../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../shared/components/confirm-dialog.component';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../shared/utils/table-state.utils';
import { createContactTableConfig, TableRowAction } from './contact-table.config';
import { ContactService } from '../../services/contact.service';
import { ActualSelected, Utils } from '../../../../shared/utils';
import { consts } from '../../../../consts';
import { LoadingDialogService } from '../../../../shared/ui/loading/loading-dialog.service';
import { UserCompanyService } from '../../../../modules/user/usercompany/usercompany.service';
import { AccountingAccountsService } from '../../../../modules/accounting/accounting-accounts/accounting-accounts.service';
import { ConsultantService } from '../../../../modules/consultant/consultant.service';
import { RegisterModel } from '../../../../core/authentication/register/register.model';
import { ContactService as OldContactService } from '../../../../modules/contact/contact/contact.service';
import { ContactDialogTComponent } from '../../../../modules/contact/contact/contact-dialog-t.component';
import { ContactBankAccountListComponent } from '../../../../modules/contact/contactbankaccounts/contactbankaccount-list.component';
import { CenterListComponent } from '../../../../modules/contact/center/center-list.component';
import { ContactImportComponent } from '../../../../modules/contact/contact/contact-import.component';
import { ContactFormV2Component } from '../contact-form-v2/contact-form-v2.component';
import { AccountingAccountsDialogLedgerComponent } from '../../../../modules/accounting/accounting-accounts/accounting-accounts-dialog-ledger.component';
import { BookingContactSyncDialogComponent } from '../../../../modules/contact/contact/booking-contact-sync-dialog.component';

@Component({
    selector: 'app-contact-list-v2',
    templateUrl: './contact-list.component.html',
    styleUrls: ['./contact-list.component.css'],
    standalone: false
})
export class ContactListV2Component implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('factuoneCell', { static: false }) factuoneCell!: TemplateRef<any>;
  @ViewChild('directCell', { static: false }) directCell!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'perm_identity';
  title = 'Contactos';
  subTitle = 'Consulta, edita o gestiona tus clientes y proveedores';

  contacts: any[] = [];
  showCreateUserOption: boolean = ActualSelected.isConsultant;

  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 10,
    filters: {},
    sortField: '',
    sortDirection: ''
  };

  private destroy$ = new Subject<void>();

  constructor(
    private contactService: ContactService,
    private oldContactService: OldContactService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    public dialog: MatDialog,
    private userCompanyService: UserCompanyService,
    private accountingAccountsService: AccountingAccountsService,
    private consultantService: ConsultantService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    let defaultFilter: any = {};
    const stored = localStorage.getItem('viewContactFilters');
    if (stored) {
      try {
        defaultFilter = JSON.parse(stored) || {};
        if (defaultFilter.pageSize == null) defaultFilter.pageSize = 10;
      } catch (e) {
        defaultFilter = {};
      }
    }

    this.tableConfig = createContactTableConfig({
      actionMap: {
        [TableRowAction.Edit]: (row) => this.editContact(row),
        [TableRowAction.Delete]: (row) => this.deleteContact(row),
        [TableRowAction.History]: (row) => this.openContactT(row),
        [TableRowAction.Statement]: (row) => this.extractoContable(row),
        [TableRowAction.LedgerClient]: (row) => this.openLedger(row, 'client'),
        [TableRowAction.LedgerProvider]: (row) => this.openLedger(row, 'provider'),
        [TableRowAction.BankAccounts]: (row) => this.contactBankAccounts(row.uid),
        [TableRowAction.Centers]: (row) => this.listCenters(row),
        [TableRowAction.CreateUser]: (row) => this.createUser(row),
        [TableRowAction.SyncBookcit]: (row) => this.syncBookcit(row),
      },
      columns: [
        {
          key: 'type',
          label: 'Tipo',
          sortable: false,
          badge: {
            variantFn: (_: any, row: any) => row.isClient && row.isProvider ? 'brand' : row.isClient ? 'success' : row.isProvider ? 'info' : 'neutral',
            labelFn: (_: any, row: any) => this.getContactType(row),
          },
        },
        { key: 'companyName', label: 'Razón social', sortable: true, sortKey: 'CompanyName', onCellClick: (row) => this.editContact(row) },
        { key: 'vatNumber', label: 'CIF/NIF', sortable: true, sortKey: 'VatNumber' },
        {
          key: 'accountNumberClient', label: 'Cuenta contable cliente', sortable: true, sortKey: 'AccountNumberClient',
          onCellClick: (row) => row.accountNumberClient ? this.openLedger(row, 'client') : null,
        },
        {
          key: 'accountNumberProvider', label: 'Cuenta contable proveedor/acreedor', sortable: true, sortKey: 'AccountNumberProvider',
          onCellClick: (row) => row.accountNumberProvider ? this.openLedger(row, 'provider') : null,
        },
        { key: 'phone', label: 'Teléfono', sortable: true, sortKey: 'Phone' },
        { key: 'inConvfact', label: 'Tiene Handly', sortable: false, customTemplate: this.factuoneCell },
        { key: 'isDirect', label: 'Conexión directa', sortable: false, customTemplate: this.directCell },
      ],
      selectable: false,
      headerActions: [
        { label: '', icon: 'add', type: 'button', callback: () => this.newContact() },
      ],
      gridActions: [
        { label: 'Informe 347', icon: 'description', type: 'button', callback: () => this.generateReport347() },
        { label: 'Importar contactos', icon: 'file_upload', type: 'button', callback: () => this.importContacts() },
        { label: 'Exportar a Excel', icon: 'file_download', type: 'button', callback: () => this.exportToExcel() },
      ],
      rowConfig: {},
    },
      {
        filterView: 'viewContactFilters',
        defaultFilter,
        showCreateUserOption: this.showCreateUserOption
      },
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    (this.tableConfig.columns || []).forEach(col => {
      if (col.key === 'inConvfact') col.customTemplate = this.factuoneCell;
      if (col.key === 'isDirect') col.customTemplate = this.directCell;
    });
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(s => (s.filters && (s.filters as any).search) || ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(v => { if (v !== this.quickSearch) this.quickSearch = v as string; });

    this.restoreStoredState();

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        this.lastState = state;
        localStorage.setItem('viewContactFilters', JSON.stringify(state));
        this.loadContacts();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  private restoreStoredState(): void {
    const stored = this.tableConfig.defaultFilter as Partial<TableStateSnapshot>;
    if (!stored) return;

    const tableState = this.t1.tableState;
    if (stored.pageSize) tableState.setPageSize(stored.pageSize);
    if (stored.filters && Object.keys(stored.filters).length) tableState.setFilters(stored.filters);
    if (stored.sortField) tableState.setSort(stored.sortField, stored.sortDirection || '');
  }

  private loadContacts(): void {
    this.t1.tableState.setLoading(true);

    this.contactService.getSummaryList(buildSearchPayload(this.lastState))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (!response || (response.errors && response.errors.length)) {
            this.t1.tableState.setLoading(false);
            this.notify.error('No se pudieron cargar los contactos');
            return;
          }
          const expanded = new Set((this.t1.tableState.snapshot.data || []).filter((r: any) => r.__expanded).map((r: any) => r.uid));
          const items = (response.values || []).map((item: any) => ({ ...item, __expanded: expanded.has(item.uid) }));
          this.contacts = items;
          this.t1.tableState.setData(items);
          this.t1.tableState.setTotalItems(response.total);
          this.t1.tableState.setLoading(false);
        },
        error: () => {
          this.t1.tableState.setLoading(false);
          this.notify.error('No se pudieron cargar los contactos');
        }
      });
  }

  private requireTier(): boolean {
    if (!Utils.IsTierFour()) {
      this.notify.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return false;
    }
    return true;
  }

  newContact(): void {
    this.loading.run('Preparando formulario…', this.userCompanyService.loadData())
      .then(company => {
        if (company && company.accountLength == 0) {
          this.notify.warning('Debe ajustar la longitud de sus cuentas contables antes de crear un contacto.');
          return;
        }
        this.openContactForm(undefined);
      })
      .catch(() => this.notify.error('No se pudo comprobar la configuración de cuentas contables'));
  }

  editContact(row: any): void {
    this.openContactForm(row.uid);
  }

  private openContactForm(uid: string | undefined): void {
    ContactFormV2Component.open(this.dialog, { uid }).subscribe(result => {
      if (result) this.loadContacts();
    });
  }

  deleteContact(row: any): void {
    if (!this.requireTier()) return;

    this.confirm.open({
      title: 'Eliminar contacto',
      subtitle: row.companyName || undefined,
      message: 'Se eliminará el contacto. Si tiene facturas, gastos u otros documentos asociados no se podrá eliminar. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.contactDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando contacto…',
      errorText: 'No se pudo eliminar el contacto',
      action: () => this.oldContactService.deleteData(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Contacto eliminado correctamente');
      this.loadContacts();
    });
  }

  openContactT(row: any): void {
    if (!this.requireTier()) return;

    ContactDialogTComponent.open(this.dialog, {
      uid: row.uid,
      clientAccount: row.accountNumberClient || null,
      providerAccount: row.accountNumberProvider || null,
    });
  }

  contactBankAccounts(uid: string): void {
    if (!this.requireTier()) return;

    ContactBankAccountListComponent.open(this.dialog, { uid });
  }

  listCenters(row: any): void {
    if (!this.requireTier()) return;

    CenterListComponent.open(this.dialog, { contactUid: row.uid, showFaceFields: row.isJuridical === true || row.isPublicAdmin === true });
  }

  importContacts(): void {
    if (!this.requireTier()) return;

    this.dialog.open(ContactImportComponent, {
      width: '760px',
      maxWidth: '96vw',
      maxHeight: '90vh',
    }).afterClosed().subscribe(() => this.loadContacts());
  }

  exportToExcel(): void {
    if (!this.requireTier()) return;

    this.loading.run('Exportando contactos…', this.oldContactService.export())
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetExcelName('Contactos'));
        this.notify.ok('Contactos exportados correctamente');
      })
      .catch(() => this.notify.error('No se pudieron exportar los contactos'));
  }

  generateReport347(): void {
    if (!this.requireTier()) return;

    this.loading.run('Generando informe 347…', this.oldContactService.report347(consts.EMPTY_GUID))
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetExcelName('Informe 347'));
        this.notify.ok('Informe 347 generado correctamente');
      })
      .catch(() => this.notify.error('No se pudo generar el informe 347'));
  }

  extractoContable(row: any): void {
    if (!this.requireTier()) return;

    this.loading.run('Generando extracto contable…', this.oldContactService.excelExtractoContable(row.uid))
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetExcelName('Extracto contable'));
        this.notify.ok('Extracto contable exportado correctamente');
      })
      .catch(() => this.notify.error('No se pudo exportar el extracto contable'));
  }

  openLedger(row: any, kind: 'client' | 'provider'): void {
    if (!this.requireTier()) return;

    const accountNumber = kind === 'client' ? row.accountNumberClient : row.accountNumberProvider;
    const missing = kind === 'client'
      ? 'Este contacto no dispone de cuenta contable de cliente.'
      : 'Este contacto no dispone de cuenta contable de proveedor.';
    if (!accountNumber) {
      this.notify.warning(missing);
      return;
    }

    this.loading.run('Buscando cuenta contable…', this.accountingAccountsService.list(0, 5, accountNumber))
      .then((response: any) => {
        const accounts: any[] = (response && response.data) || [];
        const account = accounts.find(a => String(a.number) === String(accountNumber)) || (accounts.length === 1 ? accounts[0] : null);
        if (!account) {
          this.notify.warning(missing);
          return;
        }
        this.dialog.open(AccountingAccountsDialogLedgerComponent, {
          width: '700px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: { id: account.id, name: account.name, number: account.number }
        });
      })
      .catch(() => this.notify.warning(missing));
  }

  createUser(row: any): void {
    if (!this.requireTier()) return;

    if (!row.email) {
      this.notify.warning('El contacto necesita un email para crear su cuenta Handly.');
      return;
    }

    const register: RegisterModel = {
      CompanyName: row.companyName,
      VatNumber: row.vatNumber,
      Username: row.email,
      Password: '',
      Country: row.country,
      ProvinceCode: row.provinceCode,
      IsConsultant: false,
      Newsletter: false,
      State: row.state,
      Name: '',
      Uid: consts.EMPTY_GUID,
      ParametrizationId: this.getParametrization(row.vatNumber),
      PhoneNumber: row.phone,
      countriesCode: row.countriesCode,
    };

    this.confirm.open({
      title: 'Crear cuenta Handly',
      subtitle: row.companyName || undefined,
      message: 'Se dará de alta una cuenta Handly para este contacto con su email como usuario.',
      tone: 'info',
      icon: 'person_add',
      details: [
        { label: 'Email', value: row.email },
        ...(row.vatNumber ? [{ label: 'CIF/NIF', value: row.vatNumber }] : []),
      ],
      confirmText: 'Crear cuenta',
      confirmIcon: 'person_add',
      loadingText: 'Creando cuenta…',
      errorText: 'No se pudo crear la cuenta',
      action: () => this.consultantService.registerUser(register).pipe(map((result: any) => {
        this.notify.ok(result && result['message'] ? result['message'] : 'Cuenta creada correctamente');
        return result;
      })),
    }).subscribe();
  }

  syncBookcit(row: any): void {
    this.dialog.open(BookingContactSyncDialogComponent, {
      width: '640px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data: { contactUid: row.uid, contactName: row.companyName || '' },
    });
  }

  getContactType(row: any): string {
    if (row.isClient && row.isProvider) return 'Cliente y proveedor';
    if (row.isClient) return 'Cliente';
    if (row.isProvider) return 'Proveedor/Acreedor';
    return 'Sin tipo';
  }

  private contactDetails(row: any): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.vatNumber) details.push({ label: 'CIF/NIF', value: row.vatNumber });
    details.push({ label: 'Tipo', value: this.getContactType(row) });
    if (row.accountNumberClient) details.push({ label: 'Cuenta cliente', value: row.accountNumberClient });
    if (row.accountNumberProvider) details.push({ label: 'Cuenta proveedor', value: row.accountNumberProvider });
    return details;
  }

  private getParametrization(vatNumber: string): number {
    const juridical: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'N', 'R', 'U', 'V', 'W'];
    const physical: string[] = ['X', 'Y', 'Z', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const first = (vatNumber || '').charAt(0).toUpperCase();

    if (physical.indexOf(first) !== -1) return 3;
    if (juridical.indexOf(first) !== -1) return 2;
    return 1003;
  }
}
